// --- BASE ---
import React, { FormEvent, useState } from 'react';
import ReactDOM from 'react-dom';
import qs from 'qs';

// --- BASE DEV ---
import { faker } from '@faker-js/faker';

// --- COMPONENTS ---
import AlertBox from '../components/AlertBox';
import CompanyInfo from './components/CompanyInfo';
import FillValues from './components/FillValues';
import LoginInfo from './components/LoginInfo';

// --- HELPERS ---
import { checkExpired } from '../Helpers';

// --- INTERFACES ---
import { IErrors, IRequest } from '../Interfaces';

interface IParams {
	api_hostname: string;
	request_enabled: boolean | string;
}

interface IProps {
	params: IParams;
}

// *** FUNCTIONAL COMPONENT ***
const Request = (props: IProps) => {
	const { params }: IProps = { ...props };

	// --- TOKEN ---
	const token = localStorage.getItem('token');

	// if (token === null || token === '') {
	// 	window.location.assign('/overview');
	// }

	// --- CONSTANTS ---
	const apiHostname = params.api_hostname;

	// --- STATES ---
	const [alert, setAlert] = useState<object>({});
	const [asset, setAsset] = useState<IRequest>({
		country_code: 'US'
	});
	const [disabled, setDisabled] = useState<boolean>(false);
	const [errors, setErrors] = useState<IErrors>({});
	const [hideForm, setHideForm] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	// --- LOCAL FUNCTIONS ---
	function handleChange(name: string, value: boolean | number | object | string | null | undefined) {
		// console.log(name, value);

		const newAsset: IRequest = asset === null ? ({} as IRequest) : { ...asset };

		newAsset[name] = value ?? null;

		setAsset(newAsset);
	}

	function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();

		setDisabled(true);
		setIsLoading(true);

		let url = apiHostname + '/accounts/request';

		let method = 'POST';
		if (asset !== null && 'id' in asset && typeof asset.id === 'number' && asset.id !== null) {
			method = 'PUT';
			url += '/' + asset.id.toString();
		}

		const queryParams = {};

		if (Object.keys(queryParams).length > 0) {
			url += '?' + qs.stringify(queryParams);
		}

		fetch(url, {
			headers: {
				'Accept': 'application/json',
				'Authorization': 'Bearer ' + token,
				'Content-Type': 'application/json'
			},
			method: method,
			mode: 'cors',
			body: JSON.stringify(asset)
		})
			.then((response) => response.json())
			.then((result) => {
				// --- CHECK EXPIRED ---
				// checkExpired(result);

				const data = typeof result === 'object' && result !== null && 'data' in result && typeof result.data === 'object' && result.data !== null ? result.data : null;
				const errors: IErrors = result !== null && 'errors' in result && typeof result.errors === 'object' && result.errors !== null ? result.errors : {};
				const message = typeof result === 'object' && result !== null && 'message' in result && typeof result.message === 'string' && result.message !== null ? result.message : 'Unknown error.';
				const status = typeof result === 'object' && result !== null && 'status' in result && typeof result.status === 'string' && result.status !== null ? result.status : 'error';

				setErrors(errors);

				if (status === 'success') {
					if (data !== null && 'token' in data && typeof data.token === 'string' && data.token !== null) {
						localStorage.setItem('token', data.token);
					}

					setHideForm(true);
				}

				setAlert({
					message: message,
					show: true,
					status: status
				});
			})
			.finally(() => {
				setDisabled(false);
				setIsLoading(false);
			});
	}

	// --- USE EFFECTS ---

	return (
		<div>
			<div
				className="loading"
				hidden={!isLoading}>
				Loading...
			</div>

			<AlertBox
				alert={alert}
				onClose={() => setAlert({})}
			/>

			<div hidden={hideForm}>
				<p>Please fill out the following form to request an account on MovinCool Resellers. Once we receive your information, one of our representatives will review your details and get back to you shortly.</p>

				<form
					autoComplete="off"
					onSubmit={handleSubmit}>
					<LoginInfo
						asset={asset}
						disabled={disabled}
						errors={errors}
						onChange={handleChange}
					/>

					<CompanyInfo
						asset={asset}
						disabled={disabled}
						errors={errors}
						onChange={handleChange}
					/>

					<fieldset className="mb-3">
						<legend>Additional Information</legend>

						<p>Enter any comments or notes you have regarding this request.</p>

						<div className="col-md-12">
							<div className="mb-3">
								<label htmlFor="notes">Notes</label>
								<textarea
									className={'form-control' + (errors?.notes ?? false ? ' is-invalid' : '')}
									disabled={disabled}
									name="notes"
									onChange={(e) => handleChange(e.currentTarget.name, e.target.value)}
									placeholder="Notes"
									rows={3}
									value={asset?.notes ?? ''}
								/>
								<div className="invalid-feedback">{errors?.notes ?? ''}</div>
							</div>
						</div>
					</fieldset>

					<div className="nav-buttons">
						<button
							className="btn btn-primary"
							disabled={disabled}
							title="Submit your entries to MovinCool."
							type="submit">
							Submit Request
						</button>

						<FillValues
							asset={asset}
							setAsset={setAsset}
						/>

						<a
							className="btn btn-secondary"
							href="/"
							onClick={() => setIsLoading(true)}>
							Cancel
						</a>
					</div>
				</form>
			</div>

			<div hidden={!hideForm}>
				Please check your email and <a href="/verify">click here</a> to enter your verification code.
			</div>
		</div>
	);
};

// --- RENDER ---
ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(<Request params={window.params} />);
